import { graphql, useStaticQuery } from 'gatsby';
import Link from 'gatsby-link';
import { chunk } from 'lodash';
import styled from 'styled-components';
import React, { Fragment, useState } from 'react';

import DebtIncrementer from '../ProofBuilders/DebtIncrementer';
import SCREENER_CTA_TYPES from '../analytics/screenerCtaTypes';
import getScreenerUrl from '../Links/getScreenerUrl';
import { Button } from '../Inputs/Button.span';
import { withAmp } from '../AMP/AmpContext';
import IconMenu from '../../../static/images/icon-menu.svg';
import Logo from '../../../static/images/upsolve-logo.svg';
import { A } from '../analytics/LinksWithOnClick';
import trackComponentAction from '../analytics/trackComponentAction';

const HelloBar = (props) => {
  let { contentGroup, helloBarTemplate = 'debtCleared', helloBarText, helloBarUrl } = props;
  const screenerUrlBar = getScreenerUrl({ cta: SCREENER_CTA_TYPES.HEADER_HELLO_BAR, contentGroup });
  return (
    <Fragment>
      {helloBarTemplate === 'debtCleared' && (
        <HelloBarWrapper>
          <a aria-busy="false" href={screenerUrlBar}>
            <u>Upsolve's helped families clear <span className="incrementer">$<DebtIncrementer /></span> of debt</u>
          </a>
        </HelloBarWrapper>
      )}
      {helloBarTemplate === 'link' && (
        <HelloBarWrapper>
          <a aria-busy="false" href={helloBarUrl || screenerUrlBar}>
            {helloBarText || 'Upsolve has cleared $120,000,000+ debt for hundreds of families'}
          </a>
        </HelloBarWrapper>
      )}
    </Fragment>
  );
}

const trackingProps = {
  componentName: 'Header',
  componentVersion: 0,
};

const Header = (props) => {
  const { contentGroup } = props;
  let { showHelloBar = true } = props;
  const screenerUrlNav = getScreenerUrl({ cta: SCREENER_CTA_TYPES.HEADER_NAV, contentGroup });
  const [isMenuShowing, setMenuShowing] = useState(false);
  const [isLearnPanelOpen, setLearnPanelOpen] = useState(false);
  const data = useStaticQuery(graphql`
    query HeaderQuery {
      links: allContentfulLearnArticle(filter: { siteLinking: { showInSiteNav: { eq: true} } }) {
        edges {
          node {
            title
            slug
            siteLinking {
              showInSiteNav
            }
          }
        }
      }
    }
  `);

  return (
    <div>
      {/* {showHelloBar !== false && <HelloBar {...props} />} */}

      <HeaderContainer color={props.color}>
        <div className="nav__lead">
          <IconMenu
            aria-busy="false"
            aria-label="Toggle Menu"
            role="button"
            className="icon-menu"
            onClick={() => setMenuShowing(true)}
          />
          <span
            role="button"
            aria-label="Toggle Menu"
          >
            <Link to="/"><Logo /></Link>
          </span>
        </div>

        <nav className={`nav__links ${isMenuShowing ? 'show' : ''}`}>
          <div className="nav__links__header">
            <label onClick={() => setMenuShowing(false)}>
              Close
            </label>
            <label>
              <A
                href={screenerUrlNav}
                onClick={() => trackComponentAction({ ...trackingProps, actionId: 'header.screenerNavigation', actionMethod: 'click' })}
              >
                Get Free Bankruptcy Help
              </A>
            </label>
          </div>
          <div>
            <Link aria-busy="false" to="/learn/how-to-file-bankruptcy/">
              How to File Bankruptcy
            </Link>
          </div>
          <div>
            <Link aria-busy="false" to="/learn/transparency/">
              How Upsolve is Free
            </Link>
          </div>
          <div
            onMouseEnter={() => setLearnPanelOpen(true)}
            onMouseLeave={() => setLearnPanelOpen(false)}
          >
            <Link aria-busy="false" to="/learn/">
              Learning Center
            </Link>
              <div className={`nav__links__list ${isLearnPanelOpen ? 'show' : ''}`}>
                {chunk(data.links.edges, 6).map((chunk, chunkIndex) => (
                  <ul key={chunkIndex}>
                    {chunk.map(({ node }) => (
                      <li key={node.slug}>
                        <Link
                          aria-busy="false"
                          to={`/learn/${node.slug}/`}
                          onClick={() => {
                            setLearnPanelOpen(false);
                            setMenuShowing(false);
                          }}
                        >
                          {node.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
          </div>
          <div>
            <Link aria-busy="false" to="/team/">
              Team
            </Link>
          </div>
          <div>
            <Link aria-busy="false" to="/fresh-starts/">
              Community
            </Link>
          </div>
        </nav>

        <div className="nav__ctas">
          <a aria-busy="false" href="https://my.upsolve.org/login">Login</a>
          <A
            aria-busy="false"
            href={screenerUrlNav}
            onClick={() => trackComponentAction({ ...trackingProps, actionId: 'header.screenerNavigation', actionMethod: 'click' })}
          >
            <Button size="sm" inverted>
                <span className="hide-sm">Get Free Bankruptcy Help</span>
                <span className="show-sm">Get Debt Help</span>
            </Button>
          </A>
        </div>
      </HeaderContainer>
    </div>
  );
}

const HeaderContainer = styled.header`
  background: ${props => props.theme.colors.white[900]};
  border-bottom: 1px solid ${props => props.theme.colors.white[500]};
  box-shadow: 0 5px 15px -5px rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: center;
  width: 100%;
  z-index: 999;
  svg {
    display: block;
    height: 32px;
    width: 140px;
    margin-top: -2px;
    @media(max-width: 45em) {
      height: 28px;
      width: 120px;
      margin-top: 0;
    }
    path {
      fill: ${props => props.theme.colors.brand[500]};
    }
  }
  a {
    color: ${props => props.theme.colors.gray[700]};
    &:hover {
      color: ${props => props.theme.colors.brand[500]};
    }
    font-size: 14px;
    button {
      font-weight: 700;
    }
  }

  .nav__lead, .nav__links, .nav__ctas {
    flex-grow: 1;
    height: 54px;
  }

  .nav__lead {
    max-width: 150px;
    display: inline-flex;
    align-items: center;
    margin-left: 0.75em;
    .icon-menu {
      width: 24px;
      max-width: 44px;
      display: none;
      @media(max-width: 45em) {
        display: initial;
      }
    }
  }
  .nav__links {
    // Desktop (Wide)
    & > .nav__links__header {
      display: none;
    }
    @media(min-width: 45em) {
      display: flex;
      & > div {
        display: flex;
        align-items: center;
        & > a {
          padding: 0.75em;
        }
      }
    }
    // Mobile (Slim)
    @media(max-width: 45em) {
      position: fixed;
      overflow-y: scroll;
      width: 100vw;
      height: 100vh;
      z-index: 1001;
      transform: translateX(-100vw);
      transition: 250ms;
      transition-timing-function: ease-in-out;
      background: ${props => props.theme.colors.white[900]};
      &.show {
        transform: translateX(0);
      }
      & > .nav__links__header {
        display: flex;
        justify-content: space-between;
      }
      & > div {
        padding: 1em;
        &:first-of-type {
          margin-top: 1em;
        }
        &:last-of-type {
          margin-bottom: 2.5em;
        }
        &:not(:last-of-type) {
          border-bottom: 1px solid ${props => props.theme.colors.white[300]};
        }
        & > a {
          font-size: 18px;
          color: ${props => props.theme.colors.brand[500]};
        }
      }
    }
  }
  .nav__links__list {
    // Desktop (Wide)
    @media(min-width: 45em) {
      display: none;
      width: 100vw;
      position: absolute;
      z-index: 998;
      left: 0;
      right: 0;
      top: 54px;
      background: ${props => props.theme.colors.white[800]};
      border-top: 1px solid ${props => props.theme.colors.white[500]};
      box-shadow: 0 5px 15px -5px rgba(0, 0, 25, 0.2);
      padding: 0.5em 2em;
      &.show {
        display: flex;
        align-items: flex-start;
        justify-content: center;
      }
      a {
        font-size: 13px;
      }
      ul {
        padding: 0 1em;
        list-style-type: disc;
        flex: 1;
      }
      li {
        margin-bottom: 0.2em;
        color: ${props => props.theme.colors.brand[500]};
      }
    }
    // Mobile (Slim)
    @media(max-width: 45em) {
      ul {
        padding: 0;
        margin: 0;
        &:first-of-type {
          margin-top: 1em;
        }
        &:last-of-type {
          margin-bottom: 1em;
        }
      }
      li {
        margin-bottom: 0.65em;
        list-style-type: disc;
        color: ${props => props.theme.colors.brand[500]};
        margin-left: 1.25em;
        a {
          font-size: 15px;
        }
      }
    }
  }
  .nav__ctas {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    & > * {
      margin-right: 0.75em;
    }
  }
`;

const HelloBarWrapper = styled.div`
  text-align: center;
  background: ${props => props.theme.colors.white[600]};
  display: flex;
  color: ${props => props.theme.colors.brand[500]};
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.25px;
  height: 2.75em;
  a {
    width: 100%;
    padding: .5em;
    color: ${props => props.theme.colors.brand[500]};
    text-decoration: none;
  }
  .incrementer {
    width: 400px;
    min-width: 400px;
  }

  @media(max-width: 45em) {
    font-size: 13px;
    font-weight: initial;
    letter-spacing: 0;
  }
`;

export default withAmp(Header);
